import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios";
import { Sparkles, Star, ArrowRight } from "lucide-react";
import IllustrationTile, { themeNameFromUrl } from "@/components/IllustrationTile";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function resolveUrl(u) {
  return u && u.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u;
}

export default function Hero() {
  const [heroItem, setHeroItem] = useState(null);

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/gallery?kind=hero`).then((res) => {
      if (alive && Array.isArray(res.data) && res.data.length > 0) {
        const sorted = [...res.data].sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
        setHeroItem(sorted[0]);
      }
    }).catch(() => {});
    return () => { alive = false; };
  }, []);

  const heroTheme = heroItem ? themeNameFromUrl(heroItem.url) : null;
  const heroPhotoUrl = heroItem && !heroTheme ? resolveUrl(heroItem.url) : null;

  return (
    <section className="relative overflow-hidden" data-testid="hero-section">
      {/* dotted background */}
      <div className="absolute inset-0 dotted-bg opacity-10 pointer-events-none" />
      {/* floating shapes */}
      <div className="absolute -left-10 top-20 w-40 h-40 rounded-full bg-brand-green/30 blur-2xl" />
      <div className="absolute right-0 top-40 w-52 h-52 rounded-full bg-brand-yellow/40 blur-2xl" />

      <div className="mx-auto max-w-6xl px-6 pt-14 pb-16 md:pt-20 md:pb-24 grid gap-10 md:grid-cols-2 items-center relative">
        <div>
          <span className="inline-flex items-center gap-2 rounded-full border-2 border-brand-ink bg-brand-yellow px-4 py-1.5 font-bold text-brand-ink text-sm shadow-brutal-sm" data-testid="hero-badge">
            <Sparkles size={16} /> Ridgeway, Johannesburg
          </span>

          <h1 className="mt-5 font-display text-5xl md:text-7xl font-bold leading-[1.02] tracking-tight text-brand-ink">
            Where little <span className="text-brand-red">learners</span>
            <br />
            <span className="text-brand-ink">grow into </span>
            <span className="text-brand-green">bright stars</span>
            <span className="text-brand-yellow">.</span>
          </h1>

          <p className="mt-5 text-lg md:text-xl text-brand-ink/80 max-w-lg leading-relaxed">
            An English-based playgroup with a warm Islamic ethos, caring for 3–4 year olds through play, learning and love — every Tuesday, Wednesday & Thursday morning.
          </p>

          <div className="mt-8 flex flex-wrap gap-4">
            <Link
              to="/#trial"
              className="inline-flex items-center gap-2 rounded-full bg-brand-green text-white font-bold px-7 py-3.5 btn-brutal"
              data-testid="hero-cta-trial"
            >
              <Sparkles size={18} fill="#fff" /> Book a free trial morning
            </Link>
            <Link
              to="/#enroll"
              className="inline-flex items-center gap-2 rounded-full bg-brand-red text-white font-bold px-7 py-3.5 btn-brutal"
              data-testid="hero-cta-enroll"
            >
              Enroll your little one <ArrowRight size={18} />
            </Link>
            <Link
              to="/games"
              className="inline-flex items-center gap-2 rounded-full bg-white text-brand-ink font-bold px-7 py-3.5 btn-brutal"
              data-testid="hero-cta-play"
            >
              Play a game <Star size={18} className="text-brand-yellow" fill="#FFD166" />
            </Link>
          </div>

          <div className="mt-10 flex items-center gap-6 text-brand-ink/80">
            <div>
              <p className="font-display text-3xl font-bold text-brand-red leading-none">20+</p>
              <p className="text-sm font-semibold">Years of joy</p>
            </div>
            <div className="w-px h-10 bg-brand-ink/20" />
            <div>
              <p className="font-display text-3xl font-bold text-brand-green leading-none">3–4</p>
              <p className="text-sm font-semibold">Age group</p>
            </div>
            <div className="w-px h-10 bg-brand-ink/20" />
            <div>
              <p className="font-display text-3xl font-bold text-brand-yellow leading-none">3x</p>
              <p className="text-sm font-semibold">Per week</p>
            </div>
          </div>
        </div>

        <div className="relative">
          {/* Sticker badge */}
          <div className="absolute -top-6 -left-6 z-20 rotate-[-8deg] bg-brand-red text-white font-display font-bold rounded-full border-[3px] border-brand-ink w-28 h-28 flex flex-col items-center justify-center shadow-brutal-sm animate-bounce-soft" data-testid="hero-20-years-badge">
            <span className="text-3xl leading-none">20</span>
            <span className="text-xs tracking-wider">YEARS!</span>
          </div>

          {/* Second sticker */}
          <div className="absolute -bottom-4 -right-2 z-20 rotate-[10deg] bg-brand-green text-white font-display font-bold rounded-2xl border-[3px] border-brand-ink px-4 py-2 shadow-brutal-sm">
            Est. Ridgeway
          </div>

          <div className="relative rounded-[36px] overflow-hidden border-[4px] border-brand-ink shadow-brutal-lg bg-white h-[420px] md:h-[520px]" data-testid="hero-image-container">
            {heroPhotoUrl ? (
              <img
                src={heroPhotoUrl}
                alt={heroItem?.label || "Cheeky Monkeys playgroup"}
                className="w-full h-full object-cover"
                data-testid="hero-image"
              />
            ) : (
              <IllustrationTile theme={heroTheme || "abc"} />
            )}
          </div>

          {/* Sparkles */}
          <span className="absolute -left-3 top-1/3 text-brand-yellow animate-sparkle" style={{ animationDelay: "0.2s" }}>
            <Sparkles size={26} fill="#FFD166" />
          </span>
          <span className="absolute right-8 -top-8 text-brand-red animate-sparkle" style={{ animationDelay: "0.7s" }}>
            <Sparkles size={22} fill="#EF476F" />
          </span>
        </div>
      </div>
    </section>
  );
}
